/**
 * DP92 Part A — ONE RESOLUTION FUNCTION FOR THE SKIN, AND ONE APPLIER.
 *
 * Before this file, `index.js:16` was the only writer of `data-km-skin` and it
 * knew one rule: dark class => night, else studio. Gallery was measured, correct
 * and unreachable (DP69 ruling 5). The firm's own choice now feeds the same
 * single decision:
 *
 *   user dark mode ON            -> night
 *   user dark mode OFF           -> the firm's skin
 *   missing / unreadable / other -> studio
 *
 * Nothing else in the app may decide a skin. The firm skin and the surface
 * overrides are cached in localStorage so the very first paint — which happens
 * before any authenticated request can answer — is already the firm's own look
 * rather than a flash of studio.
 */

import { TYPE_LIBRARY, familyStack } from '@/data/typeLibrary';

export const FIRM_SKIN_KEY = 'keyMadeFirmSkin';
export const SURFACES_KEY = 'keyMadeSurfaces';
export const TYPE_KEY = 'keyMadeType';

/**
 * DP92 Part C — THE THREE TYPE ROLES.
 *
 * Stored ONCE, not per skin. A face reads the same on a dark desk as on a light
 * one — a skin changes colour, not letterforms — and a firm that picked Fraunces
 * for its names has picked it for its names, not for its daylight. The surfaces
 * are per skin because a light hex is meaningless on night; a family is not.
 *
 * `dflt` is what the token file ships and what an unknown face falls back to.
 * `role` is the library role the face SHOULD have; assignment is not restricted
 * to it — a firm may put a display face on names, and FIGURES warns rather than
 * refusing.
 */
export const TYPE_ROLES = [
  { id: 'names', label: 'NAMES', field: 'type_names', varName: '--km-display',
    role: 'n', dflt: 'Bodoni Moda', what: 'project names, headlines, the masthead' },
  { id: 'sentences', label: 'SENTENCES', field: 'type_sentences', varName: '--km-body',
    role: 'b', dflt: 'Archivo', what: 'anything a person wrote' },
  { id: 'figures', label: 'FIGURES', field: 'type_figures', varName: '--km-tech',
    role: 'f', dflt: 'IBM Plex Mono', what: 'money, dates, counts, clocks — 43 places' },
  { id: 'labels', label: 'LABELS', field: 'type_labels', varName: '--km-label',
    role: 'f', dflt: 'IBM Plex Mono', what: 'the small mono captions and chips — 124 places' },
];

/** The only two a firm may choose. Night is the dark-mode skin, never a choice. */
export const FIRM_SKINS = ['STUDIO', 'GALLERY'];

/**
 * THE FIVE SURFACES, outermost first. `varName` is the token each control
 * writes; the shipped value of that token is the reset target.
 */
export const SURFACE_LAYERS = [
  { id: 'outer', label: 'OUTER', varName: '--km-outer', what: 'the mat the app sits on' },
  { id: 'frame', label: 'FRAME', varName: '--km-rail', what: 'the boundary around the app' },
  { id: 'surface', label: 'SURFACE', varName: '--km-field-2', what: 'the ground under the cards' },
  { id: 'card', label: 'CARD', varName: '--km-paper', what: 'a card' },
  { id: 'cardHead', label: 'CARD HEAD', varName: '--km-paper-2', what: "a card's head" },
];

export const isHex = (v) =>
  typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v.trim());

export const resolveSkin = (darkMode, firmSkin) => {
  if (darkMode) return 'night';
  const key = typeof firmSkin === 'string' ? firmSkin.trim().toUpperCase() : '';
  return key === 'GALLERY' ? 'gallery' : 'studio';
};

const readStore = (key) => {
  try { return localStorage.getItem(key); } catch (_e) { return null; }
};

export const DAYLIGHT_ATTR = 'data-km-daylight';

const readDarkMode = () =>
  !document.documentElement.hasAttribute(DAYLIGHT_ATTR)
  && (document.documentElement.classList.contains('dark')
      || readStore('keyMadeDarkMode') === 'true');

const readOverrides = (skin) => {
  try {
    const parsed = JSON.parse(readStore(SURFACES_KEY) || 'null');
    const forSkin = parsed && typeof parsed === 'object' ? parsed[skin] : null;
    return forSkin && typeof forSkin === 'object' ? forSkin : {};
  } catch (_e) { return {}; }
};

/**
 * Read the SHIPPED value of each surface token for a skin, out of the stylesheet
 * itself. Deliberately not a table of hex values in JS: a second copy of the
 * palette would drift from keymade-tokens.css, and a raw colour in a component is
 * exactly what the design-token guard exists to refuse.
 *
 * Reads the CSSOM rather than swapping <body data-km-skin> and measuring. The
 * swap version LOCKED THE PAGE: BrandingSurfacesCard watches that attribute, and
 * a read that writes the attribute re-triggered the watcher forever. Found by the
 * Branding tab going unresponsive to every click; recorded here so it is not
 * reintroduced.
 */
export const shippedSurfaces = (skin) => {
  const out = {};
  const wanted = SURFACE_LAYERS.map((l) => l.varName);
  const match = (selector) => {
    if (!selector) return false;
    if (selector.includes(`[data-km-skin="${skin}"]`)) return true;
    // studio is also the :root default, so a bare :root block is studio's
    return skin === 'studio' && /(^|,)\s*:root\s*($|,)/.test(selector);
  };
  for (const sheet of Array.from(document.styleSheets)) {
    let rules;
    try { rules = sheet.cssRules; } catch (_e) { continue; }
    for (const rule of Array.from(rules || [])) {
      if (!rule.style || !match(rule.selectorText)) continue;
      wanted.forEach((name) => {
        const v = rule.style.getPropertyValue(name).trim();
        if (v) out[name] = v;
      });
    }
  }
  const byId = {};
  SURFACE_LAYERS.forEach((l) => { byId[l.id] = out[l.varName] || ''; });
  return byId;
};

/**
 * Write the resolved skin onto <body> and apply the firm's surface overrides
 * for that skin. An override that is missing or not a 6-digit hex is REMOVED,
 * so the token falls back to the skin's shipped value — deleting the field is a
 * working studio, not a broken page.
 */
export const applySkin = () => {
  const skin = resolveSkin(readDarkMode(), readStore(FIRM_SKIN_KEY));
  document.body.setAttribute('data-km-skin', skin);
  const overrides = readOverrides(skin);
  SURFACE_LAYERS.forEach((layer) => {
    const value = overrides[layer.id];
    if (isHex(value)) document.body.style.setProperty(layer.varName, value.trim());
    else document.body.style.removeProperty(layer.varName);
  });
  return skin;
};

/** Cache what the tenant record says, then re-apply. Safe with a null record. */
export const cacheBranding = (settings) => {
  try {
    const firm = settings?.firm_skin;
    localStorage.setItem(FIRM_SKIN_KEY, FIRM_SKINS.includes(String(firm).toUpperCase()) ? String(firm).toUpperCase() : 'STUDIO');
    const surfaces = settings?.surface_overrides;
    if (surfaces && typeof surfaces === 'object') localStorage.setItem(SURFACES_KEY, JSON.stringify(surfaces));
    else localStorage.removeItem(SURFACES_KEY);
    const type = {};
    TYPE_ROLES.forEach((r) => { type[r.field] = settings?.[r.field] || ''; });
    localStorage.setItem(TYPE_KEY, JSON.stringify(type));
  } catch (_e) { /* a blocked localStorage must not stop the app painting */ }
  applyType();
  return applySkin();
};

/* ---- the type roles (DP92 Part C) ---------------------------------- */

/** The library role a face carries, or null if the face is not in the library. */
export const faceRole = (name) => {
  const row = TYPE_LIBRARY.find((f) => f[0] === name);
  return row ? row[1] : null;
};

/**
 * THE ONE PLACE A ROLE'S FACE IS DECIDED. An unknown, empty or unreadable value
 * becomes that role's shipped default, silently — the same rule as the surfaces.
 */
export const resolveFace = (roleId, value) => {
  const role = TYPE_ROLES.find((r) => r.id === roleId);
  if (!role) return null;
  const name = typeof value === 'string' ? value.trim() : '';
  return name && faceRole(name) ? name : role.dflt;
};

/**
 * Write the three families onto <body>, through familyStack() from
 * data/typeLibrary.js and nothing else. When a role is on its shipped default the
 * inline property is REMOVED rather than set, so the token file's own stack
 * applies untouched — familyStack('Bodoni Moda') drops the Didot in
 * `--km-display`, and default must mean identical, not equivalent.
 */
export const applyType = () => {
  let stored = {};
  try { stored = JSON.parse(readStore(TYPE_KEY) || '{}') || {}; } catch (_e) { stored = {}; }
  const applied = {};
  TYPE_ROLES.forEach((role) => {
    const face = resolveFace(role.id, stored[role.field]);
    applied[role.id] = face;
    if (face === role.dflt) document.body.style.removeProperty(role.varName);
    else document.body.style.setProperty(role.varName, familyStack(face));
  });
  return applied;
};

/* ---- contrast, reported never enforced (DP92 Part B) ---------------- */

export const rgbOf = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const channel = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

export const luminance = (hex) => {
  const rgb = rgbOf(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(channel);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * A native <input type="color"> needs SOME value and a DOM value attribute
 * cannot take a CSS variable, so the fallback is read OUT of the token file at
 * runtime rather than copied into a component. Same trick as
 * ProjectColourPicker's neutralInk().
 */
export const tokenValue = (name) =>
  getComputedStyle(document.body).getPropertyValue(name).trim();

export const contrast = (a, b) => {  const la = luminance(a);
  const lb = luminance(b);
  if (la === null || lb === null) return null;
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
};
